import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import ReactFlow, {
    Background, Controls, MiniMap, Handle, Position, MarkerType,
    useNodesState, type Node, type Edge,
} from "reactflow";
import "reactflow/dist/style.css";
import {
    Workflow, WfState, WfTransition, WfRoles, WfValidators, WfPostActions,
    MOCK_WORKFLOWS, formatMinutes,
} from "./workflow-data";
import "../messages/TicketPage.css";

type TabKey = "designer" | "rules" | "automations" | "analytics";

// Otomasyonlar menusundeki Rules / Webhooks / n8n ogeleri buraya yonlenir;
// path'e gore acilacak sekme belirlenir.
function tabFromPath(pathname: string): TabKey {
    if (pathname.endsWith("/rules")) return "rules";
    if (pathname.endsWith("/webhooks") || pathname.endsWith("/n8n")) return "automations";
    return "designer";
}

const CAT_LABEL: Record<string, string> = {
    new: "Başlangıç", active: "Aktif", pending: "Beklemede", done: "Çözüm", closed: "Kapalı",
};
const ROLE_LABEL: Record<keyof WfRoles, string> = { admin: "Admin", manager: "Manager", agent: "Agent", customer: "Müşteri" };
const VAL_LABEL: Record<keyof WfValidators, string> = {
    assignee: "Atanan kişi boş olamaz", group: "Destek grubu boş olamaz",
    resolutionNote: "Çözüm notu zorunlu", attachment: "Dosya zorunlu",
};
const PA_LABEL: Record<keyof WfPostActions, string> = {
    mail: "Mail gönder", slaStart: "SLA başlat", notifyOwner: "Ticket sahibine bildir",
    webhook: "Webhook çağır", n8n: "n8n akışı çalıştır",
};

export default function WorkflowsPage() {
    const [workflows, setWorkflows] = useState<Workflow[]>(() => JSON.parse(JSON.stringify(MOCK_WORKFLOWS)));
    const [selectedWfId, setSelectedWfId] = useState<string>(MOCK_WORKFLOWS[0].id);
    const { pathname } = useLocation();
    const [tab, setTab] = useState<TabKey>(() => tabFromPath(pathname));
    const [sel, setSel] = useState<{ type: "state" | "transition"; id: string } | null>(null);

    const wf = workflows.find(w => w.id === selectedWfId) || workflows[0];
    const stateById = useMemo(() => new Map(wf.states.map(s => [s.id, s])), [wf]);

    // Rules/Webhooks/n8n menu ogeleri designer disi bir sekme ister; bu durumda
    // dogrudan tasarim gorunumune gecip ilgili sekmeyi ac.
    const [view, setView] = useState<"list" | "design">(() => tabFromPath(pathname) === "designer" ? "list" : "design");

    // Menuden farkli bir otomasyon ogesine gecilince ilgili sekmeyi ac.
    useEffect(() => {
        const next = tabFromPath(pathname);
        setTab(next);
        if (next !== "designer") setView("design");
    }, [pathname]);

    // ── immutable patch helpers ──────────────────────────────────────────────
    const patchWorkflow = (fn: (w: Workflow) => Workflow) =>
        setWorkflows(ws => ws.map(w => w.id === selectedWfId ? fn(w) : w));
    const patchState = (id: string, patch: Partial<WfState>) =>
        patchWorkflow(w => ({ ...w, states: w.states.map(s => s.id === id ? { ...s, ...patch } : s) }));
    const patchTransition = (id: string, patch: Partial<WfTransition>) =>
        patchWorkflow(w => ({ ...w, transitions: w.transitions.map(tr => tr.id === id ? { ...tr, ...patch } : tr) }));

    const addState = () => {
        const id = `s-${Date.now().toString(36)}`;
        patchWorkflow(w => ({ ...w, states: [...w.states, { id, name: "Yeni Durum", color: "#6366F1", category: "active", customerVisible: false, customerLabel: "" }] }));
        setSel({ type: "state", id });
    };
    const addTransition = (from: string, to: string) => {
        if (!from || !to || from === to) return;
        const id = `t-${Date.now().toString(36)}`;
        patchWorkflow(w => ({
            ...w,
            transitions: [...w.transitions, {
                id, name: "Yeni Geçiş", from, to,
                roles: { admin: true, manager: true, agent: true, customer: false },
                validators: { assignee: false, group: false, resolutionNote: false, attachment: false },
                postActions: { mail: false, slaStart: false, notifyOwner: false, webhook: false, n8n: false },
                avgMinutes: 0,
            }],
        }));
        setSel({ type: "transition", id });
    };
    const deleteState = (id: string) => {
        patchWorkflow(w => ({ ...w, states: w.states.filter(s => s.id !== id), transitions: w.transitions.filter(tr => tr.from !== id && tr.to !== id) }));
        setSel(null);
    };
    const deleteTransition = (id: string) => {
        patchWorkflow(w => ({ ...w, transitions: w.transitions.filter(tr => tr.id !== id) }));
        setSel(null);
    };
    const addWorkflow = () => {
        const id = `wf-${Date.now().toString(36)}`;
        const s1 = `s-${Date.now().toString(36)}`;
        setWorkflows(ws => [...ws, {
            id, name: "Yeni Workflow", ticketType: "—", active: false,
            states: [{ id: s1, name: "Yeni", color: "#3B82F6", category: "new", customerVisible: true, customerLabel: "Talebiniz Alındı" }],
            transitions: [],
        }]);
        setSelectedWfId(id); setSel(null); setTab("designer"); setView("design");
    };
    const openWorkflow = (id: string) => { setSelectedWfId(id); setSel(null); setTab("designer"); setView("design"); };

    const selState = sel?.type === "state" ? wf.states.find(s => s.id === sel.id) : null;
    const selTransition = sel?.type === "transition" ? wf.transitions.find(tr => tr.id === sel.id) : null;

    // ── Liste ekrani ──────────────────────────────────────────────────────────
    if (view === "list") {
        return <WorkflowList workflows={workflows} onOpen={openWorkflow} onNew={addWorkflow} />;
    }

    // ── Tasarim ekrani ────────────────────────────────────────────────────────
    return (
        <div className="h-full w-full flex flex-col" style={{ overflow: "hidden" }}>
            {/* Baslik: geri + workflow adi + durum ekle */}
            <div className="flex items-center gap-3 shrink-0" style={{ padding: "12px 16px 6px" }}>
                <button type="button" onClick={() => setView("list")} style={backBtnStyle}>← İş Akışları</button>
                <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>{wf.name}</h2>
                <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 6, background: "var(--base-bg-darken-4)", color: "var(--subtitle-text-color)" }}>{wf.ticketType}</span>
                {tab === "designer" && (
                    <button type="button" onClick={addState} style={{ ...newBtnStyle, marginLeft: "auto" }}>+ Durum Ekle</button>
                )}
            </div>

            <div className="flex-1 min-h-0" style={{ overflow: "hidden" }}>
                {tab === "designer" && (
                    <Designer
                        wf={wf}
                        stateById={stateById}
                        sel={sel}
                        onSelect={setSel}
                        selState={selState || null}
                        selTransition={selTransition || null}
                        patchState={patchState}
                        patchTransition={patchTransition}
                        onConnect={addTransition}
                        onDeleteState={deleteState}
                        onDeleteTransition={deleteTransition}
                    />
                )}
                {tab === "rules" && <RulesTab wf={wf} stateById={stateById} />}
                {tab === "automations" && <AutomationsTab wf={wf} stateById={stateById} />}
                {tab === "analytics" && <AnalyticsTab wf={wf} stateById={stateById} />}
            </div>
        </div>
    );
}

// ── Workflow Liste Ekrani ───────────────────────────────────────────────────
function WorkflowList({ workflows, onOpen, onNew }: { workflows: Workflow[]; onOpen: (id: string) => void; onNew: () => void }) {
    return (
        <div className="h-full w-full" style={{ overflowY: "auto", padding: 20 }}>
            <div className="flex items-center gap-3" style={{ marginBottom: 16 }}>
                <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>İş Akışları</h2>
                <button type="button" onClick={onNew} style={{ ...newBtnStyle, marginLeft: "auto" }}>+ Yeni Workflow</button>
            </div>
            <div style={{ background: "var(--base-bg)", border: "1px solid var(--border-color)", borderRadius: 10, overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead><tr style={{ textAlign: "left", fontSize: 12, color: "var(--subtitle-text-color)" }}>
                        <th style={th}>Workflow</th><th style={th}>Ticket Tipi</th><th style={th}>Durum Sayısı</th><th style={th}>Geçiş</th><th style={th}>Aktif</th>
                    </tr></thead>
                    <tbody>
                        {workflows.map(w => (
                            <tr key={w.id} onClick={() => onOpen(w.id)} className="wf-list-row"
                                style={{ borderTop: "1px solid var(--border-color)", cursor: "pointer" }}>
                                <td style={td}><strong>{w.name}</strong></td>
                                <td style={td}><span className="text-xs" style={{ color: "var(--subtitle-text-color)" }}>{w.ticketType}</span></td>
                                <td style={td}>{w.states.length}</td>
                                <td style={td}>{w.transitions.length}</td>
                                <td style={td}>
                                    <span style={{ background: w.active ? "#10B98122" : "#9CA3AF22", color: w.active ? "#10B981" : "#9CA3AF", fontWeight: 600, fontSize: 12, padding: "2px 10px", borderRadius: 999 }}>
                                        {w.active ? "✓ Aktif" : "Pasif"}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ── Designer (React Flow canvas) ───────────────────────────────────────────────
const nodeTypes = { state: StateNode };

function Designer({ wf, stateById, sel, onSelect, selState, selTransition, patchState, patchTransition, onConnect, onDeleteState, onDeleteTransition }: {
    wf: Workflow;
    stateById: Map<string, WfState>;
    sel: { type: "state" | "transition"; id: string } | null;
    onSelect: (s: { type: "state" | "transition"; id: string } | null) => void;
    selState: WfState | null;
    selTransition: WfTransition | null;
    patchState: (id: string, patch: Partial<WfState>) => void;
    patchTransition: (id: string, patch: Partial<WfTransition>) => void;
    onConnect: (from: string, to: string) => void;
    onDeleteState: (id: string) => void;
    onDeleteTransition: (id: string) => void;
}) {
    const [nodes, setNodes, onNodesChange] = useNodesState<{ state: WfState; isSelected: boolean }>([]);

    // Node'lari wf.states ile uzlastir: mevcut pozisyonu koru, yeni durumlar icin pozisyon ata,
    // silinenleri dusur. Workflow degisince de (id'ler eslesmeyince) sifirdan kurar.
    useEffect(() => {
        setNodes(nds => {
            const byId = new Map(nds.map(n => [n.id, n]));
            return wf.states.map((s, i) => {
                const existing = byId.get(s.id);
                return existing
                    ? { ...existing, data: { ...existing.data, state: s } }
                    : { id: s.id, type: "state", position: { x: 160 + (i % 2) * 60, y: i * 110 }, data: { state: s, isSelected: false } } as Node;
            });
        });
    }, [wf.states, wf.id, setNodes]);

    // Secim degisince node'larda isSelected isaretle.
    useEffect(() => {
        setNodes(nds => nds.map(n => ({ ...n, data: { ...n.data, isSelected: sel?.type === "state" && sel.id === n.id } })));
    }, [sel, setNodes]);

    const edges: Edge[] = useMemo(() => wf.transitions.map(tr => {
        const activeEdge = sel?.type === "transition" && sel.id === tr.id;
        return {
            id: tr.id, source: tr.from, target: tr.to, label: tr.name, type: "smoothstep",
            markerEnd: { type: MarkerType.ArrowClosed },
            animated: activeEdge,
            style: { strokeWidth: activeEdge ? 2.5 : 1.5, stroke: activeEdge ? "var(--accent-color, #6366F1)" : "#94A3B8" },
            labelStyle: { fontSize: 11, fill: "var(--base-text-color)" },
            labelBgStyle: { fill: "var(--base-bg)" },
        };
    }), [wf.transitions, sel]);

    return (
        <div className="flex h-full" style={{ overflow: "hidden" }}>
            {/* Canvas */}
            <div style={{ flex: 1, minWidth: 0 }}>
                <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    nodeTypes={nodeTypes}
                    onNodesChange={onNodesChange}
                    onConnect={(c) => { if (c.source && c.target) onConnect(c.source, c.target); }}
                    onNodeClick={(_, n) => onSelect({ type: "state", id: n.id })}
                    onEdgeClick={(_, e) => onSelect({ type: "transition", id: e.id })}
                    onPaneClick={() => onSelect(null)}
                    fitView
                    proOptions={{ hideAttribution: true }}
                >
                    <Background gap={16} color="#94A3B8" />
                    <Controls showInteractive={false} />
                    <MiniMap pannable zoomable nodeColor={(n: any) => n.data?.state?.color || "#888"} />
                </ReactFlow>
            </div>

            {/* Sag ozellik paneli */}
            <div style={{ width: 340, borderLeft: "1px solid var(--border-color)", overflowY: "auto", background: "var(--base-bg)" }}>
                {selState && <StatePanel state={selState} patch={patchState} onDelete={() => onDeleteState(selState.id)} />}
                {selTransition && <TransitionPanel tr={selTransition} fromName={stateById.get(selTransition.from)?.name || "?"} toName={stateById.get(selTransition.to)?.name || "?"} patch={patchTransition} onDelete={() => onDeleteTransition(selTransition.id)} />}
                {!selState && !selTransition && (
                    <div className="flex items-center justify-center h-full text-sm" style={{ color: "var(--subtitle-text-color)", padding: 24, textAlign: "center" }}>
                        Bir durum veya geçiş seçin
                    </div>
                )}
            </div>
        </div>
    );
}

// React Flow ozel node: durum karti (renk + ad + musteri ikonu, ust/alt baglanti noktalari).
function StateNode({ data }: { data: { state: WfState; isSelected: boolean } }) {
    const s = data.state;
    const selected = data.isSelected;
    return (
        <div style={{
            display: "flex", alignItems: "center", gap: 10, width: 220,
            padding: "10px 12px", borderRadius: 10,
            background: selected ? `${s.color}1f` : "var(--base-bg)",
            border: `1px solid ${selected ? s.color : "var(--border-color)"}`,
            boxShadow: selected ? `0 0 0 1px ${s.color}` : "none",
        }}>
            <Handle type="target" position={Position.Top} style={{ background: s.color }} />
            <span style={{ width: 12, height: 12, borderRadius: 4, background: s.color, flexShrink: 0 }} />
            <span style={{ fontWeight: 600, fontSize: 13, color: "var(--base-text-color)" }}>{s.name}</span>
            <span style={{ marginLeft: "auto", fontSize: 10, color: "var(--subtitle-text-color)" }}>{CAT_LABEL[s.category]}</span>
            {s.customerVisible && <i className="dx-icon-user" title="Müşteriye görünür" style={{ fontSize: 12, color: "var(--subtitle-text-color)" }} />}
            <Handle type="source" position={Position.Bottom} style={{ background: s.color }} />
        </div>
    );
}

// ── State paneli ───────────────────────────────────────────────────────────────
function StatePanel({ state, patch, onDelete }: { state: WfState; patch: (id: string, p: Partial<WfState>) => void; onDelete: () => void }) {
    const PALETTE = ["#3B82F6", "#6366F1", "#8B5CF6", "#F59E0B", "#F97316", "#10B981", "#EF4444", "#6B7280"];
    return (
        <div style={{ padding: 16 }}>
            <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
                <PanelTitle>Durum Özellikleri</PanelTitle>
                <button type="button" onClick={onDelete} title="Durumu sil" style={deleteBtnStyle}><i className="dx-icon-trash" /></button>
            </div>
            <Field label="Durum Adı">
                <input style={inputStyle} value={state.name} onChange={e => patch(state.id, { name: e.target.value })} />
            </Field>
            <Field label="Renk">
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {PALETTE.map(c => (
                        <button key={c} type="button" onClick={() => patch(state.id, { color: c })} style={{
                            width: 24, height: 24, borderRadius: 6, background: c, cursor: "pointer",
                            border: state.color === c ? "2px solid var(--base-text-color)" : "1px solid var(--border-color)",
                        }} />
                    ))}
                </div>
            </Field>
            <Field label="Kategori">
                <select style={inputStyle} value={state.category} onChange={e => patch(state.id, { category: e.target.value as WfState["category"] })}>
                    {Object.entries(CAT_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
            </Field>
            <Check label="Müşteriye Göster" checked={state.customerVisible} onChange={v => patch(state.id, { customerVisible: v })} />
            {state.customerVisible && (
                <Field label="Müşteri Adı (görünen)">
                    <input style={inputStyle} value={state.customerLabel} onChange={e => patch(state.id, { customerLabel: e.target.value })} />
                </Field>
            )}
        </div>
    );
}

// ── Transition paneli ────────────────────────────────────────────────────────
function TransitionPanel({ tr, fromName, toName, patch, onDelete }: { tr: WfTransition; fromName: string; toName: string; patch: (id: string, p: Partial<WfTransition>) => void; onDelete: () => void }) {
    const setGroup = <K extends "roles" | "validators" | "postActions">(group: K, key: string, value: boolean) =>
        patch(tr.id, { [group]: { ...(tr[group] as any), [key]: value } } as Partial<WfTransition>);
    return (
        <div style={{ padding: 16 }}>
            <div className="flex items-center justify-between">
                <PanelTitle>Geçiş Özellikleri</PanelTitle>
                <button type="button" onClick={onDelete} title="Geçişi sil" style={deleteBtnStyle}><i className="dx-icon-trash" /></button>
            </div>
            <div className="text-xs" style={{ color: "var(--subtitle-text-color)", marginBottom: 10 }}>{fromName} → {toName}</div>
            <Field label="Geçiş Adı">
                <input style={inputStyle} value={tr.name} onChange={e => patch(tr.id, { name: e.target.value })} />
            </Field>

            <SectionLabel>Kim Yapabilir?</SectionLabel>
            {(Object.keys(ROLE_LABEL) as (keyof WfRoles)[]).map(k => (
                <Check key={k} label={ROLE_LABEL[k]} checked={tr.roles[k]} onChange={v => setGroup("roles", k, v)} />
            ))}

            <SectionLabel>Validatorlar</SectionLabel>
            {(Object.keys(VAL_LABEL) as (keyof WfValidators)[]).map(k => (
                <Check key={k} label={VAL_LABEL[k]} checked={tr.validators[k]} onChange={v => setGroup("validators", k, v)} />
            ))}

            <SectionLabel>Post Actions</SectionLabel>
            {(Object.keys(PA_LABEL) as (keyof WfPostActions)[]).map(k => (
                <Check key={k} label={PA_LABEL[k]} checked={tr.postActions[k]} onChange={v => setGroup("postActions", k, v)} />
            ))}

            {tr.postActions.n8n && (
                <div style={{ marginTop: 10, padding: 10, borderRadius: 8, background: "var(--base-bg-darken-4)", border: "1px solid var(--border-color)" }}>
                    <div className="text-xs" style={{ color: "var(--subtitle-text-color)", marginBottom: 4 }}>n8n Webhook</div>
                    <code style={{ fontSize: 11, wordBreak: "break-all" }}>https://n8n.mobi/webhook/ticket/{tr.id}</code>
                </div>
            )}
        </div>
    );
}

// ── Kurallar sekmesi ───────────────────────────────────────────────────────────
function RulesTab({ wf, stateById }: { wf: Workflow; stateById: Map<string, WfState> }) {
    return (
        <div style={{ padding: 16, overflowY: "auto", height: "100%" }}>
            <div style={{ background: "var(--base-bg)", border: "1px solid var(--border-color)", borderRadius: 10, overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead><tr style={{ textAlign: "left", fontSize: 12, color: "var(--subtitle-text-color)" }}>
                        <th style={th}>Geçiş</th><th style={th}>Akış</th><th style={th}>Roller</th><th style={th}>Validatorlar</th>
                    </tr></thead>
                    <tbody>
                        {wf.transitions.map(tr => (
                            <tr key={tr.id} style={{ borderTop: "1px solid var(--border-color)" }}>
                                <td style={td}><strong>{tr.name}</strong></td>
                                <td style={td}><span className="text-xs" style={{ color: "var(--subtitle-text-color)" }}>{stateById.get(tr.from)?.name} → {stateById.get(tr.to)?.name}</span></td>
                                <td style={td}>{(Object.keys(ROLE_LABEL) as (keyof WfRoles)[]).filter(k => tr.roles[k]).map(k => ROLE_LABEL[k]).join(", ") || "—"}</td>
                                <td style={td}>{(Object.keys(VAL_LABEL) as (keyof WfValidators)[]).filter(k => tr.validators[k]).length || "—"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ── Otomasyonlar sekmesi ───────────────────────────────────────────────────────
function AutomationsTab({ wf, stateById }: { wf: Workflow; stateById: Map<string, WfState> }) {
    return (
        <div style={{ padding: 16, overflowY: "auto", height: "100%", display: "flex", flexDirection: "column", gap: 10 }}>
            {wf.transitions.map(tr => {
                const enabled = (Object.keys(PA_LABEL) as (keyof WfPostActions)[]).filter(k => tr.postActions[k]);
                return (
                    <div key={tr.id} style={{ background: "var(--base-bg)", border: "1px solid var(--border-color)", borderRadius: 10, padding: 14 }}>
                        <div style={{ fontWeight: 600 }}>{tr.name} <span className="text-xs" style={{ color: "var(--subtitle-text-color)", fontWeight: 400 }}>· {stateById.get(tr.from)?.name} → {stateById.get(tr.to)?.name}</span></div>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
                            {enabled.length ? enabled.map(k => (
                                <span key={k} style={{ fontSize: 12, padding: "3px 10px", borderRadius: 999, background: "var(--base-bg-darken-4)", border: "1px solid var(--border-color)" }}>
                                    {PA_LABEL[k]}
                                </span>
                            )) : <span className="text-xs" style={{ color: "var(--subtitle-text-color)" }}>Otomasyon yok</span>}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

// ── Analitik sekmesi ───────────────────────────────────────────────────────────
function AnalyticsTab({ wf, stateById }: { wf: Workflow; stateById: Map<string, WfState> }) {
    const total = wf.transitions.reduce((a, tr) => a + tr.avgMinutes, 0);
    return (
        <div style={{ padding: 16, overflowY: "auto", height: "100%" }}>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3" style={{ marginBottom: 16 }}>
                <Metric label="Durum Sayısı" value={wf.states.length} color="#6366F1" />
                <Metric label="Geçiş Sayısı" value={wf.transitions.length} color="#0EA5E9" />
                <Metric label="Toplam Ort. Süre" value={formatMinutes(total)} color="#10B981" />
            </div>
            <div className="text-sm font-semibold uppercase" style={{ letterSpacing: "0.04em", color: "var(--subtitle-text-color)", marginBottom: 8 }}>Ortalama Geçiş Süreleri</div>
            <div style={{ background: "var(--base-bg)", border: "1px solid var(--border-color)", borderRadius: 10, overflow: "hidden" }}>
                {wf.transitions.map(tr => (
                    <div key={tr.id} className="flex items-center justify-between" style={{ padding: "10px 14px", borderTop: "1px solid var(--border-color)" }}>
                        <span className="text-sm">{stateById.get(tr.from)?.name} → {stateById.get(tr.to)?.name}</span>
                        <strong style={{ fontSize: 13 }}>{formatMinutes(tr.avgMinutes)}</strong>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ── Ortak küçük bileşenler ─────────────────────────────────────────────────────
function Metric({ label, value, color }: { label: string; value: string | number; color: string }) {
    return (
        <div style={{ background: "var(--base-bg)", border: "1px solid var(--border-color)", borderLeft: `4px solid ${color}`, borderRadius: 12, padding: "14px 16px" }}>
            <div style={{ fontSize: 26, fontWeight: 700, color }}>{value}</div>
            <div className="text-xs" style={{ color: "var(--subtitle-text-color)", marginTop: 2 }}>{label}</div>
        </div>
    );
}
function PanelTitle({ children }: { children: React.ReactNode }) {
    return <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>{children}</div>;
}
function SectionLabel({ children }: { children: React.ReactNode }) {
    return <div className="text-xs font-semibold uppercase" style={{ letterSpacing: "0.04em", color: "var(--subtitle-text-color)", margin: "14px 0 6px" }}>{children}</div>;
}
function Field({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div style={{ marginBottom: 12 }}>
            <label className="block text-xs" style={{ marginBottom: 4, color: "var(--subtitle-text-color)" }}>{label}</label>
            {children}
        </div>
    );
}
function Check({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
    return (
        <label className="flex items-center gap-2 text-sm" style={{ padding: "4px 0", cursor: "pointer" }}>
            <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
            <span style={{ color: "var(--base-text-color)" }}>{label}</span>
        </label>
    );
}

const inputStyle: React.CSSProperties = {
    width: "100%", padding: "7px 10px", borderRadius: 6, fontSize: 13,
    border: "1px solid var(--border-color)", background: "var(--base-bg)", color: "var(--base-text-color)",
};
const selectStyle: React.CSSProperties = { ...inputStyle, width: "auto", minWidth: 220 };
const newBtnStyle: React.CSSProperties = {
    padding: "7px 14px", borderRadius: 8, fontSize: 13, fontWeight: 600,
    background: "var(--accent-color, #6366F1)", color: "#fff", border: "none", cursor: "pointer",
};
const backBtnStyle: React.CSSProperties = {
    padding: "5px 12px", borderRadius: 8, fontSize: 13, fontWeight: 500,
    background: "var(--base-bg-darken-4)", color: "var(--base-text-color)", border: "1px solid var(--border-color)", cursor: "pointer",
};
const deleteBtnStyle: React.CSSProperties = {
    width: 28, height: 28, borderRadius: 6, fontSize: 13,
    background: "#EF444415", color: "#EF4444", border: "1px solid #EF444455", cursor: "pointer",
};
const th: React.CSSProperties = { padding: "10px 14px", fontWeight: 600 };
const td: React.CSSProperties = { padding: "10px 14px", fontSize: 13, verticalAlign: "middle" };
